import { useMemo } from "react"
import { Button } from "@/components/ui/button"
import type { ImportField } from "@/hooks/use-data-imports"

interface StepMappingProps {
  csvHeaders: string[]
  csvPreview: string[][]
  availableFields: ImportField[]
  fieldMapping: Record<string, string>
  onMappingChange: (mapping: Record<string, string>) => void
  onNext: (mapping: Record<string, string>) => void
  onBack: () => void
  entityType: string
}

export function StepMapping({
  csvHeaders,
  csvPreview,
  availableFields,
  fieldMapping,
  onMappingChange,
  onNext,
  onBack,
  entityType,
}: StepMappingProps) {
  const requiredFields = useMemo(
    () => availableFields.filter((f) => f.required).map((f) => f.key),
    [availableFields]
  )

  const mappedRequiredFields = useMemo(() => {
    const mappedValues = Object.values(fieldMapping)
    return requiredFields.filter((rf) => mappedValues.includes(rf))
  }, [fieldMapping, requiredFields])

  const allRequiredMapped = requiredFields.length === mappedRequiredFields.length

  const handleFieldChange = (csvColumn: string, targetField: string) => {
    const newMapping = { ...fieldMapping }
    if (targetField === "") {
      delete newMapping[csvColumn]
    } else {
      newMapping[csvColumn] = targetField
    }
    onMappingChange(newMapping)
  }

  const handleNext = () => {
    onNext(fieldMapping)
  }

  // Get a sample value for a csv column
  const getSampleValue = (colIndex: number): string => {
    for (const row of csvPreview) {
      if (row[colIndex]?.trim()) return row[colIndex].trim()
    }
    return ""
  }

  const entityLabel = entityType === "companies" ? "kontrahentow" : "kontaktow"

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Przypisz kolumny z pliku CSV do pol {entityLabel} w systemie.
        Pola oznaczone <span className="text-destructive">*</span> sa wymagane.
      </p>

      {/* Mapping table */}
      <div className="max-h-[400px] overflow-y-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 sticky top-0">
            <tr>
              <th className="text-left font-medium px-3 py-2">Kolumna CSV</th>
              <th className="text-left font-medium px-3 py-2">Przyklad</th>
              <th className="text-left font-medium px-3 py-2">Pole docelowe</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {csvHeaders.map((header, idx) => (
              <tr key={header} className="hover:bg-muted/30">
                <td className="px-3 py-2">
                  <span className="font-medium text-foreground">{header}</span>
                </td>
                <td className="px-3 py-2">
                  <span className="text-muted-foreground text-xs truncate max-w-[150px] inline-block">
                    {getSampleValue(idx) || "\u2014"}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <select
                    value={fieldMapping[header] || ""}
                    onChange={(e) => handleFieldChange(header, e.target.value)}
                    className="w-full h-8 rounded-md border border-input bg-transparent px-2 text-sm outline-none"
                  >
                    <option value="">-- Pomin --</option>
                    {availableFields.map((field) => {
                      const isAlreadyMapped =
                        Object.entries(fieldMapping).some(
                          ([col, val]) => val === field.key && col !== header
                        )
                      return (
                        <option
                          key={field.key}
                          value={field.key}
                          disabled={isAlreadyMapped}
                        >
                          {field.label}
                          {field.required ? " *" : ""}
                          {isAlreadyMapped ? " (juz zmapowane)" : ""}
                        </option>
                      )
                    })}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Required fields status */}
      {!allRequiredMapped && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3">
          <p className="text-sm text-destructive">
            Wymagane pola nie sa zmapowane:{" "}
            {requiredFields
              .filter((rf) => !mappedRequiredFields.includes(rf))
              .map((rf) => {
                const field = availableFields.find((f) => f.key === rf)
                return field?.label || rf
              })
              .join(", ")}
          </p>
        </div>
      )}

      {/* Data preview */}
      {csvPreview.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Podglad danych (pierwsze wiersze)</h4>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  {csvHeaders.map((h) => (
                    <th key={h} className="text-left font-medium px-2 py-1.5 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {csvPreview.slice(0, 3).map((row, i) => (
                  <tr key={i} className="hover:bg-muted/30">
                    {row.map((cell, j) => (
                      <td key={j} className="px-2 py-1.5 text-muted-foreground whitespace-nowrap max-w-[150px] truncate">
                        {cell || "\u2014"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Wstecz
        </Button>
        <Button onClick={handleNext} disabled={!allRequiredMapped}>
          Dalej
        </Button>
      </div>
    </div>
  )
}
